import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import AgentLayout from '../../../components/agent/layout/AgentLayout';
import AgentChatMessageList from '../../../components/agent/chat/AgentChatMessageList';
import AgentOptionsBar from '../../../components/agent/options/AgentOptionsBar';
import { flattenOptionItems } from '../../../components/agent/options/agentOptionsUtils';
import { useAgentChatSession, useAgentSessionList } from '../../../components/agent/hooks';
import useSubscriptionStatus from '../../../hooks/useSubscriptionStatus';
import GeneralError from '../../../components/errors/GeneralError';
import { paths, routes } from '../../../utils/constants/routes';
import { MAX_SIDEBAR_RECENT_CHATS } from '../../../utils/constants/agent';
import { AGENT_ENRICHMENT_ENABLED } from '../../../utils/constants/featureFlags';
import { ENRICHMENT_REPLY_PREFIX } from '../../../utils/constants/agentEnrichment';
import { useConfirmDialog } from '../../../components/dialogs/ConfirmDialog';
import {
  eventMessageRerunClicked,
  eventMessageStopClicked,
  eventAgentEnrichmentOptionsShown,
  eventAgentEnrichmentOptionSelected
} from '../../../ga4/ga4';

// 🔥 CONVERTED TO PURE REACT - NO observer() wrapper needed!
const AgentChatPage = ({ isLoading: propIsLoading = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { chatId } = useParams();

  // Keyboard shortcut for search (Cmd/Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        handleSearchConversations();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // DEBUG: Component mount and chatId tracking
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🔍 DEBUG CHAT PAGE: =========================');
      console.log('🔍 DEBUG CHAT PAGE: Component mounted/updated');
      console.log('🔍 DEBUG CHAT PAGE: chatId from useParams:', chatId);
      console.log('🔍 DEBUG CHAT PAGE: Timestamp:', new Date().toISOString());
      console.log('🔍 DEBUG CHAT PAGE: =========================');
    }
  }, [chatId]);

  // Agent session management - session-scoped hook
  const {
    session,
    messages,
    connectionStatus,
    agentState,
    isLoading: sessionIsLoading,
    isSending,
    error,
    searchMetadata,
    sendMessage,
    stopMessage,
    sendControlReply,
    retryMessage,
    clearError,
    isConnected,
    sessionUnavailable,
    sessionCheckFailed
  } = useAgentChatSession(chatId);

  // DEBUG: Monitor hook results
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🔍 DEBUG CHAT PAGE: useAgentChatSession hook results changed:', {
        chatId,
        sessionExists: !!session,
        sessionId: session?.id,
        sessionTitle: session?.title,
        messagesCount: messages?.length || 0,
        connectionStatus,
        agentState,
        sessionIsLoading,
        isSending,
        error: error || 'none',
        timestamp: new Date().toISOString()
      });

      if (messages && messages.length > 0) {
        console.log(
          '🔍 DEBUG CHAT PAGE: Messages in session:',
          messages.map((m) => ({
            id: m.id,
            type: m.type,
            content: m.content ? m.content.substring(0, 50) + '...' : 'No content'
          }))
        );
      }
    }
  }, [chatId, session, messages, connectionStatus, agentState, sessionIsLoading, isSending, error]);

  // DEBUG: Specific agentState monitoring
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      console.log(
        '🔍 DEBUG CHAT PAGE: Agent state changed to:',
        agentState,
        'at',
        new Date().toISOString()
      );
    }

    if (agentState === 'thinking') {
      if (import.meta.env.DEV) console.log('🔍 DEBUG CHAT PAGE: ⚠️ AGENT IS THINKING - monitoring for stuck state');

      // Set up timeout to detect stuck thinking state
      const stuckThinkingTimer = setTimeout(() => {
        console.error('🔍 DEBUG CHAT PAGE: 🚨 AGENT STUCK IN THINKING STATE for 10 seconds!', {
          chatId,
          agentState,
          connectionStatus,
          messagesCount: messages?.length || 0,
          timestamp: new Date().toISOString()
        });
      }, 10000); // 10 second timeout

      return () => clearTimeout(stuckThinkingTimer);
    }
  }, [agentState, chatId, connectionStatus, messages]);

  // Session list for sidebar
  const {
    allSessions,
    updateSession,
    deleteSession,
    refreshSessions,
    isLoading: isLoadingSessionList
  } = useAgentSessionList();

  // TD-2571: what failed is the session-list fetch, so that is what we retry. Once the list lands
  // and the thread appears, useAgentChatSession's auto-load effect opens it on its own.
  const handleRetryLoadSession = () => {
    refreshSessions().catch((retryError) => {
      console.error('❌ Failed to reload conversations:', retryError);
    });
  };

  // Confirmation dialog for delete actions
  const { showConfirm } = useConfirmDialog();

  // Sidebar handlers
  const handleNewChat = () => {
    // Navigate to agent welcome page for new chat (per navigation pattern)
    navigate(paths.AGENT);
  };

  const handleSearchConversations = () => {
    // Navigate to all conversations page for search
    navigate(paths.AGENT_CONVERSATIONS);
  };

  const handleBrowseByProject = () => {
    if (import.meta.env.DEV) console.log('Browse by project clicked');
    // TODO: Implement browse by project functionality
    // Could filter sessions by projects
  };

  const handleFavorites = () => {
    navigate(paths.AGENT_FAVORITES);
  };

  const handleArchived = () => {
    navigate(paths.AGENT_ARCHIVED);
  };

  const handleShowMoreRecentChats = () => {
    navigate(paths.AGENT_CONVERSATIONS);
  };

  const handleChatClick = (selectedChatId) => {
    // Navigate to specific chat
    navigate(routes.AGENT_CHAT_PATH.replace(':chatId', selectedChatId));
  };

  // Session context menu handlers
  const handleRenameChat = async (selectedChatId, newName) => {
    // If newName is not provided (legacy call from sidebar context menu), prompt user
    if (newName === undefined) {
      const sessionToRename = allSessions.find((s) => s.id === selectedChatId);
      if (!sessionToRename) return;
      newName = prompt('Enter new name for this conversation:', sessionToRename.title);
      if (!newName || newName.trim() === '') return;
    }

    try {
      await updateSession(selectedChatId, { name: newName.trim() });
      if (import.meta.env.DEV) console.log('✅ Chat renamed successfully:', selectedChatId);
    } catch (error) {
      console.error('❌ Failed to rename chat:', error);
      alert('Failed to rename conversation. Please try again.');
    }
  };

  const handleToggleChatFavorite = async (selectedChatId) => {
    const sessionToToggle = allSessions.find((s) => s.id === selectedChatId);
    if (!sessionToToggle) return;

    try {
      await updateSession(selectedChatId, { is_favorite: !sessionToToggle.isFavorite });
      if (import.meta.env.DEV) console.log('✅ Chat favorite toggled:', selectedChatId);
    } catch (error) {
      console.error('❌ Failed to toggle favorite:', error);
      alert('Failed to update favorite status. Please try again.');
    }
  };

  const handleArchiveChat = async (selectedChatId) => {
    if (!allSessions.some((s) => s.id === selectedChatId)) return;

    try {
      await updateSession(selectedChatId, { is_archived: true });
      if (import.meta.env.DEV) console.log('✅ Chat archived:', selectedChatId);
      // If archiving the current chat, navigate away
      if (selectedChatId === chatId) {
        navigate(paths.AGENT);
      }
    } catch (error) {
      console.error('❌ Failed to archive chat:', error);
      alert('Failed to archive conversation. Please try again.');
    }
  };

  const handleDeleteChat = async (selectedChatId) => {
    const sessionToDelete = allSessions.find((s) => s.id === selectedChatId);
    if (!sessionToDelete) return;

    const confirmed = await showConfirm({
      title: 'Delete Conversation',
      message: `Are you sure you want to delete "${sessionToDelete.title}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });

    if (confirmed) {
      try {
        await deleteSession(selectedChatId);
        if (import.meta.env.DEV) console.log('✅ Chat deleted:', selectedChatId);
        // If deleting the current chat, navigate away
        if (selectedChatId === chatId) {
          navigate(paths.AGENT);
        }
      } catch (error) {
        console.error('❌ Failed to delete chat:', error);
        alert('Failed to delete conversation. Please try again.');
      }
    }
  };

  // Chat input handlers
  // TD-1911: Use component-level isReadyToSend (includes agentState checks)
  // to prevent duplicate submissions while agent is processing
  const handleChatSubmit = (message) => {
    if (!message.trim() || !isReadyToSend) {
      return;
    }

    // Concurrent-session enforcement now happens at login (TD-2239), not per send.
    clearError();
    sendMessage(message).catch((error) => {
      console.error('Failed to send message:', error);
      // Error is already stored in the hook state
    });
  };

  // TD-2346: Rerun a previous user message — resubmit its exact text as a new turn
  // at the end of the thread, carrying sourceMessageId for the "Rerun of …" subtitle.
  // Uses the same readiness gate as a normal send (busy/searching ⇒ blocked, Q2).
  const handleRerunMessage = (message) => {
    if (!message?.content?.trim() || !isReadyToSend) {
      return;
    }
    clearError();
    eventMessageRerunClicked(message.id);
    sendMessage(message.content, { sourceMessageId: message.id }).catch((error) => {
      console.error('Failed to rerun message:', error);
    });
  };

  // TD-2347: stop the in-flight turn (abort stream + halt search) and log the click.
  const handleStopMessage = () => {
    eventMessageStopClicked();
    stopMessage();
  };

  const handleChatUpload = () => {
    if (import.meta.env.DEV) console.log('File upload clicked');
    // TODO: Implement file upload logic
  };

  // Get recent chats for sidebar - prioritize current chat and limit to 5
  // Always show non-archived sessions (no filter toggle)
  const recentChats = (() => {
    const allRecentSessions = allSessions || []; // Use pre-loaded sessions from hook
    const currentSession = session; // Current session from useAgentChatSession

    // Filter: exclude archived sessions by default
    const nonArchivedSessions = allRecentSessions.filter((s) => !s.isArchived);

    // Start with current session if it exists and is not archived
    const orderedSessions = [];
    if (currentSession && !currentSession.isArchived) {
      orderedSessions.push(currentSession);
    }

    // Add other non-archived sessions (excluding current one to avoid duplicates)
    const otherSessions = nonArchivedSessions.filter((s) => s.id !== chatId);
    orderedSessions.push(...otherSessions);

    // Limit to MAX_SIDEBAR_RECENT_CHATS and map to expected format
    return orderedSessions.slice(0, MAX_SIDEBAR_RECENT_CHATS).map((s) => ({
      id: s.id,
      label: s.title,
      isFavorite: s.isFavorite || false,
      isArchived: s.isArchived || false
    }));
  })();

  // Determine overall loading state
  const isLoading = propIsLoading || sessionIsLoading || isSending;

  // TD-2119 (R3): block sending when the company's total token pool is
  // exhausted. The sidebar shows the "On hold" treatment; the chat input
  // disables here as the second half of the on-hold UX. BE is expected to
  // also reject the request server-side as a defence-in-depth layer.
  const { tokens, enrichment, refetch: refetchSubscription } = useSubscriptionStatus();
  const tokensExhausted = tokens?.totalExhausted === true;

  // TD-1911: SINGLE SOURCE OF TRUTH for send readiness.
  // All submit handlers MUST reference this variable — never create local copies.
  // The provider also enforces this via agentBusyRef as a second safety layer.
  // TD-2571: gate on the session-scoped isConnected, not the global connectionStatus. The status
  // is shared across the provider, so a 'connected' left behind by a previously-opened chat
  // enabled the composer on a thread that had never loaded.
  const isReadyToSend =
    isConnected &&
    !isSending &&
    agentState === 'idle' &&
    !sessionIsLoading &&
    !tokensExhausted;

  // TD-2347: a turn is in progress whenever we're sending or the agent is not idle
  // (thinking / responding / searching). Drives the Send↔Stop swap in the input bar.
  const isAgentRunning = isSending || agentState !== 'idle';

  // TD-2293: input-area slot = enrichment credit chip + (when the latest complete
  // AI turn carries an [AGENT_OPTIONS] payload) the docked options bar. Options
  // auto-clear once the user replies — a newer message becomes the last one.
  // Gated by the feature flag; the chip self-hides for non-entitled users.
  const lastMessage = messages?.length ? messages[messages.length - 1] : null;
  const activeOptions =
    AGENT_ENRICHMENT_ENABLED &&
    lastMessage &&
    (lastMessage.type === 'ai' || lastMessage.type === 'agent')
      ? (lastMessage.enrichment?.options ?? null)
      : null;
  const activeOptionsId = activeOptions?.id ?? null;

  // GA4: fire once per distinct options set surfaced (metadata only — kind + count).
  React.useEffect(() => {
    if (activeOptions) {
      eventAgentEnrichmentOptionsShown(
        activeOptions.kind || '',
        flattenOptionItems(activeOptions).length
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOptionsId]);

  const handleOptionReply = (reply) => {
    if (!reply?.trim() || !isReadyToSend) return;
    clearError();
    eventAgentEnrichmentOptionSelected(activeOptions?.kind || '');
    sendMessage(reply).catch((error) => {
      console.error('Failed to send option reply:', error);
    });
  };

  // TD-2293 (#2a): keep the enrichment credit chip in sync after a reveal spends.
  // Two completion shapes since the TD-2281 async fix:
  //  - async job (pending single or bulk): RevealProgressCard fires
  //    onRevealComplete when the job settles → refetch here.
  //  - synchronous single reveal (no job): the value + charge arrive as the next
  //    VISIBLE agent message after CONFIRM_REVEAL. Hidden control replies do NOT
  //    move agentState (the adapter skips state notifications for hidden turns),
  //    so we reconcile off that message's arrival: arm on confirm, then refetch
  //    when a new visible agent message lands. getMessages() filters hidden
  //    turns, so the hidden confirm/poll messages don't bump the count.
  const awaitingRevealReconcileRef = React.useRef(false);
  const reconcileBaselineCountRef = React.useRef(0);

  const handleControlReply = React.useCallback(
    (reply) => {
      if (typeof reply === 'string' && reply.startsWith(ENRICHMENT_REPLY_PREFIX.CONFIRM_REVEAL)) {
        awaitingRevealReconcileRef.current = true;
        reconcileBaselineCountRef.current = messages?.length ?? 0;
      }
      sendControlReply(reply);
    },
    [sendControlReply, messages]
  );

  // One spend, one forced request: refetchSubscription is called directly. It
  // used to be wrapped in a refreshUsage() that also published
  // `subscription-usage-change`, because the sidebar usage panel held its own
  // /subscription-status instance and would otherwise have shown a stale
  // balance. Both read one shared snapshot now, so the publish would only make
  // the panel force a second request for the same deduction — and a forced load
  // deliberately never joins one in flight.
  React.useEffect(() => {
    if (!awaitingRevealReconcileRef.current) return;
    const msgs = messages ?? [];
    if (msgs.length <= reconcileBaselineCountRef.current) return;
    const last = msgs[msgs.length - 1];
    if (last && (last.type === 'ai' || last.type === 'agent')) {
      awaitingRevealReconcileRef.current = false;
      refetchSubscription();
    }
  }, [messages, refetchSubscription]);

  // TD-2883: the contact-credit chip that used to sit here is gone — the
  // balance now lives permanently in the sidebar usage panel, and the reveal /
  // export dialogs keep their own counters where the number is part of the
  // decision. What is left in this slot is the docked options bar.
  const inputTopSlot =
    AGENT_ENRICHMENT_ENABLED && activeOptions ? (
      <AgentOptionsBar
        options={activeOptions}
        onSubmit={handleOptionReply}
        disabled={!isReadyToSend}
      />
    ) : null;

  // DEBUG: Log input state calculation
  React.useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🔍 DEBUG CHAT INPUT: Input state calculation:', {
        connectionStatus,
        isSending,
        agentState,
        sessionIsLoading,
        isReadyToSend,
        chatInputDisabled: !isReadyToSend,
        timestamp: new Date().toISOString()
      });
    }
  }, [connectionStatus, isSending, agentState, isReadyToSend]);

  // Get appropriate placeholder text
  const getChatPlaceholder = () => {
    // TD-2119 R3 — on-hold message takes precedence over operational reasons
    if (tokensExhausted) return 'AI features on hold — contact sales';
    if (!isReadyToSend) {
      if (sessionIsLoading) return 'Loading agent...';
      if (!isConnected) return 'Loading conversation...';
      if (agentState === 'searching') return 'Searching for companies...';
      if (agentState !== 'idle') return 'Agent is working...';
    }
    return 'Type your message...';
  };

  // TD-2571: a chat URL must resolve to exactly one of three outcomes — it opens, we say plainly
  // that it is unavailable, or we admit we could not load it and offer a retry. Never an
  // indeterminate state. Computed as one value rather than nested JSX ternaries to keep the
  // render readable.
  const renderChatBody = () => {
    if (sessionUnavailable) {
      return (
        <GeneralError
          errorMessage="This conversation isn't available."
          actionMessage="It may have been deleted, or it belongs to a different account. You can:"
          actionButtontext="Start a new chat"
          onActionButtonClick={handleNewChat}
          withGoBackButton
        />
      );
    }

    if (sessionCheckFailed) {
      return (
        <GeneralError
          errorMessage="We couldn't load your conversations."
          actionMessage="You may want to:"
          actionButtontext="Try again"
          onActionButtonClick={handleRetryLoadSession}
          withGoBackButton
        />
      );
    }

    return (
      <AgentChatMessageList
        isLoading={isLoading}
        messages={messages}
        agentState={agentState}
        connectionStatus={connectionStatus}
        searchMetadata={searchMetadata}
        error={error}
        onRetry={retryMessage}
        onRerun={handleRerunMessage}
        rerunDisabled={!isReadyToSend}
        onControlReply={handleControlReply}
        onRevealComplete={refetchSubscription}
        creditLimit={enrichment?.limit ?? null}
      />
    );
  };

  // No composer on a thread that cannot be opened — a disabled input box on a dead conversation
  // is just another thing that looks like it might come back.
  const canUseComposer = !sessionUnavailable && !sessionCheckFailed;

  return (
    <AgentLayout
      onNewChat={handleNewChat}
      onSearchConversations={handleSearchConversations}
      onBrowseByProject={handleBrowseByProject}
      onFavorites={handleFavorites}
      onArchived={handleArchived}
      onShowMoreRecentChats={handleShowMoreRecentChats}
      onChatClick={handleChatClick}
      onRenameChat={handleRenameChat}
      onToggleChatFavorite={handleToggleChatFavorite}
      onArchiveChat={handleArchiveChat}
      onDeleteChat={handleDeleteChat}
      recentChats={recentChats}
      isLoadingRecentChats={isLoadingSessionList}
      activeRoute={location.pathname}
      showChatInput={canUseComposer}
      onChatSubmit={handleChatSubmit}
      onChatUpload={handleChatUpload}
      onChatStop={handleStopMessage}
      chatRunning={isAgentRunning}
      chatPlaceholder={getChatPlaceholder()}
      chatInputDisabled={!isReadyToSend}
      chatInputAutoFocus={true}
      inputTopSlot={inputTopSlot}
    >
      <div className="agent-chat-messages-area">{renderChatBody()}</div>
    </AgentLayout>
  );
};

export default AgentChatPage;
